/**
 * Shared parser-review / patient-reconciliation contract used by BOTH the web app and the native
 * mobile app. Web imports it in the mobile API route + the staging model; mobile imports it in the
 * Vibrant API client + hooks. Keeping the decision vocabulary and the item shape here is what stops
 * the route, the web model, and the mobile client from drifting apart (they used to each declare
 * their own copy). Types + consts only — safe for the client bundle.
 */

/**
 * A patient's answer on one reconciliation item.
 * - `confirm`  — still accurate → charts it active.
 * - `inactive` — true in the past but no longer current → charts it as history.
 * - `deny`     — not true → discards it.
 */
export const PATIENT_RECONCILIATION_DECISIONS = ["confirm", "inactive", "deny"] as const;

export type PatientReconciliationDecision = (typeof PATIENT_RECONCILIATION_DECISIONS)[number];

/** The clinical class of a reconciliation item — drives the label shown to the patient. */
export type PatientReconciliationKind = "medication" | "allergy" | "problem";

/** One reconciliation item the patient confirms, marks inactive, or denies. */
export interface PatientReconciliationItem {
  reviewSetId: string;
  valueId: string;
  label: string;
  kind: PatientReconciliationKind;
  documentTitle: string;
}

/**
 * Result of recording one decision.
 * - `ok`        — the decision was recorded and charted.
 * - `not-owner` — the set isn't the requesting member's.
 * - `not-found` — the set/item is gone, isn't reconciliation-class, or was already decided.
 * - `failed`    — the chart write threw; the item is still pending and the patient should retry.
 */
export interface PatientReconciliationDecisionResult {
  status: "ok" | "not-owner" | "not-found" | "failed";
}
